import { TrndsClientTiktokAudioViewStats, getTrndsClientTiktokAudioViewStats } from '@round/api';
import { useCallback, useState } from 'react';

type UseClientTiktokAudioViewStatsReturn = {
    viewStats: TrndsClientTiktokAudioViewStats | null;
    isLoading: boolean;
    hasError: boolean;
    isInitialized: boolean;
    refresh: (requestInit?: RequestInit) => Promise<void>;
    init: (requestInit?: RequestInit) => Promise<void>;
    reset: () => void;
};

export function useClientTiktokAudioViewStats({
    clientId,
    audioId,
}: {
    clientId: number | undefined;
    audioId: number | undefined;
}): UseClientTiktokAudioViewStatsReturn {
    const [viewStats, setViewStats] = useState<TrndsClientTiktokAudioViewStats | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            if (!clientId || !audioId) {
                return;
            }
            setIsLoading(true);
            try {
                const response = await getTrndsClientTiktokAudioViewStats({ clientId, audioId }, requestInit);
                setViewStats(response.data);
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                setHasError(true);
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, audioId]
    );
    return {
        viewStats,
        isLoading: isLoading || (!isInitialized && !hasError),
        hasError,
        isInitialized,
        refresh: fetchData,
        init: fetchData,
        reset: () => {
            setViewStats(null);
            setIsInitialized(false);
            setIsLoading(false);
            setHasError(false);
        },
    };
}
